import Navbar from "../Navbar"
import Footer from "../Footer"
import { motion } from "framer-motion";
import tankInspection from "../../../static/images/services/tank_inspection_report.png";

const TankInspectionReport = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 60 },
    show: { opacity: 1, y: 0 },
  };

  const features = [
    {
      title: "Structural Integrity Check",
      description: "Thorough inspection of tank shell, frames, and structural components for any defects or wear.",
    },
    {
      title: "Valve & Fitting Inspection",
      description: "Detailed examination of all valves, fittings, and closures to ensure proper operation and sealing.",
    },
    {
      title: "Lining & Coating Assessment",
      description: "Professional evaluation of internal lining and external coating condition to prevent contamination and corrosion.",
    },
    {
      title: "Safety Equipment Verification",
      description: "Inspection of pressure relief devices, gauges, and all safety-related equipment for compliance.",
    },
    {
      title: "Leakage & Pressure Testing",
      description: "Comprehensive pressure and leakage tests to certify tank integrity for safe cargo operations.",
    },
    {
      title: "Regulatory Compliance",
      description: "Ensuring all inspections and reports comply with IMDG, IMO, and international transport regulations.",
    },
  ];

  const benefits = [
    "Full regulatory compliance",
    "Early defect detection",
    "Cargo contamination prevention",
    "Reduced operational downtime",
    "Certified inspection reports",
    "Extended tank service life",
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] mt-20 overflow-hidden">
        <img
          src={tankInspection}
          alt="Tank Inspection Report"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-black-800/90 to-transparent">
          <div className="container mx-auto px-4 h-full flex items-center">
            <div className="max-w-3xl text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Tank Inspection Report</h1>
              <p className="text-xl text-blue-100">
                Professional inspection and certification reports for ISO tank containers ensuring safety and compliance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white pt-14">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-semibold text-orange mb-6">Service Overview</h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Our Tank Inspection Report service delivers thorough, professional assessments of ISO tank containers
              to ensure full compliance with international safety and transport regulations. Our certified inspectors
              examine every aspect of the tank — from structural integrity to valve functionality — providing detailed,
              actionable reports for owners, operators, and insurers.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Each report is prepared in accordance with IMDG, IMO, and applicable national standards, giving
              stakeholders the confidence that their tank containers are fit for service and safe for cargo operations.
              We serve shipping companies, leasing firms, and cargo owners requiring reliable, independent inspection documentation.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Key Features */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-gray-50 pt-14">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-semibold text-orange mb-6 text-center">Key Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-xl font-semibold text-gray-700 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Benefits Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white pt-14">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-semibold text-orange mb-12 text-center">Why Choose Our Service</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-orange mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-lg text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Supported Tank Types */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-gray-50 pt-14">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-semibold text-orange mb-12 text-center">Tank Types We Inspect</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { title: "ISO Tank Containers", desc: "Standard intermodal tanks for liquid bulk cargo transport" },
              { title: "Chemical Tanks", desc: "Specialized tanks for hazardous and non-hazardous chemical cargo" },
              { title: "Food Grade Tanks", desc: "Tanks certified for edible liquids and food-grade products" },
            ].map((type, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl text-center">
                <h3 className="text-xl font-semibold text-gray-700 mb-3">{type.title}</h3>
                <p className="text-gray-600">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Process Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white pt-14">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-semibold text-orange mb-12 text-center">Our Process</h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mx-auto">
              {[
                {
                  num: "1",
                  title: "Initial Assessment",
                  desc: "Review of tank history, previous reports, and owner-provided documentation",
                },
                {
                  num: "2",
                  title: "Physical Inspection",
                  desc: "On-site examination of all structural, mechanical, and safety components",
                },
                {
                  num: "3",
                  title: "Testing & Measurement",
                  desc: "Pressure testing, thickness gauging, and leakage verification",
                },
                {
                  num: "4",
                  title: "Defect Documentation",
                  desc: "Photographic and written record of all findings and observations",
                },
                {
                  num: "5",
                  title: "Certified Report Issuance",
                  desc: "Detailed, certified inspection report delivered to all relevant stakeholders",
                },
              ].map((step, index) => (
                <div key={index} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-orange text-white font-semibold">
                      {step.num}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section
        id="contact"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <Footer />
      </motion.section>
    </div>
  );
};

export default TankInspectionReport;